import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MobileLayout from "../components/layout/MobileLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Clock, TrendingUp, BarChart3 } from "lucide-react";
import { Sheet, SheetContent } from "../components/ui/sheet";
import JodiBetPage from "./games/JodiBetPage";
import HarufPage from "./games/HarufPage";
import CrossBetPage from "./games/CrossBetPage";

const TABS = [
  { key: "jodi",      label: "JODI" },
  { key: "haruf",     label: "HARUF" },
  { key: "cross_bet", label: "CROSSING" },
];

/** Live-ticking countdown — focuses on TODAY's open→close window:
 *    • Before today's open   → "खुलेगा में: Xh Ym Zs"   (ticking till today's open)
 *    • Between open & close  → "आखिरी समय शेष: Xh Ym Zs" (ticking till today's close)
 *    • After today's close   → "आज बंद हो गया"            (static — no countdown)
 */
function useCloseCountdown(market) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!market?.close_time) return { label: "लोड हो रहा है…", loading: true };
  const [ch, cm] = market.close_time.split(":").map(Number);
  const [oh, om] = (market.open_time || "00:00").split(":").map(Number);
  const istNow = new Date(Date.now() + 330 * 60 * 1000);
  const cur = istNow.getUTCHours() * 3600 + istNow.getUTCMinutes() * 60 + istNow.getUTCSeconds();
  const closeSec = ch * 3600 + cm * 60;
  const openSec  = oh * 3600 + om * 60;
  let diff;
  let prefix;
  let closed = false;
  if (openSec <= closeSec) {
    // Normal daytime market (e.g. open 06:00, close 15:00)
    if (cur < openSec)      { diff = openSec - cur;  prefix = "खुलेगा में"; }
    else if (cur <= closeSec){ diff = closeSec - cur; prefix = "आखिरी समय शेष"; }
    else                     { closed = true; }
  } else {
    // Overnight market (e.g. open 22:00, close 06:00 next day)
    if (cur >= openSec || cur <= closeSec) {
      diff = cur <= closeSec ? closeSec - cur : (86400 - cur + closeSec);
      prefix = "आखिरी समय शेष";
    } else { diff = openSec - cur; prefix = "खुलेगा में"; }
  }
  if (closed) {
    return { prefix: "आज बंद हो गया", timeStr: "", loading: false, closingSoon: false, closed: true };
  }
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (h > 0 || m > 0) parts.push(`${String(m).padStart(2, "0")}m`);
  parts.push(`${String(s).padStart(2, "0")}s`);
  const timeStr = parts.join(" ");
  const isRemaining = prefix === "आखिरी समय शेष";
  return { prefix, timeStr, loading: false, closingSoon: isRemaining && diff <= 600 };
}

export default function MarketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [market, setMarket] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("jodi");

  useEffect(() => {
    api.get(`/markets/${id}`).then((m) => setMarket(m.data));
  }, [id]);

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get(`/markets/${id}/result-history?limit=30`);
      setHistory(data.history || []);
    } catch (e) { setHistory([]); }
  };

  const { prefix, timeStr, loading: cdLoading, closingSoon, closed: cdClosed } = useCloseCountdown(market);

  return (
    <MobileLayout hideBottomNav hideHeader>
      <div
        className="relative min-h-screen"
        style={{
          background:
            "radial-gradient(ellipse 90% 50% at 50% 0%, #0a1829 0%, #060e1b 50%, #040812 100%)",
          color: "#f1f5f9",
        }}
        data-testid="market-detail-page"
      >
        {/* subtle grid texture */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
             style={{ backgroundImage: "radial-gradient(rgba(56,189,248,0.6) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

        {/* ===== BIDS SECTION top bar ===== */}
        <div className="relative px-4 pt-4 pb-3 flex items-center gap-2.5" data-testid="bids-top-bar">
          <button
            onClick={() => navigate(-1)}
            data-testid="market-back"
            className="shrink-0 w-11 h-11 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 flex items-center justify-center active:scale-95 transition shadow-[0_0_18px_rgba(56,189,248,0.15)]"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="font-display font-black text-white text-lg tracking-wider">BIDS SECTION</div>
          <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
            <div
              className="h-11 pl-3 pr-4 rounded-full border border-cyan-400/40 bg-cyan-400/5 inline-flex items-center gap-2 shadow-[0_0_18px_rgba(56,189,248,0.15)]"
              data-testid="bids-wallet-chip"
            >
              <div className="w-7 h-7 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center">
                <span className="text-cyan-300 font-black text-sm">₹</span>
              </div>
              <div className="leading-tight">
                <div className="text-[9px] font-black tracking-[0.2em] text-slate-400">बैलेंस</div>
                <div className="font-display font-black text-white tabular-nums text-base" data-testid="bids-balance">
                  {Number(user?.wallet_balance ?? 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Market name (big centered white) — tap to open result history ===== */}
        <button
          onClick={openHistory}
          data-testid="market-detail-name-wrap"
          className="relative text-center mt-5 w-full active:scale-[0.99] transition"
        >
          <h1
            className="font-display font-black text-white tracking-widest leading-none"
            style={{ fontSize: "clamp(2.1rem, 9vw, 2.6rem)", textShadow: "0 0 24px rgba(56,189,248,0.4)" }}
            data-testid="market-detail-name"
          >
            {market?.name || "—"}
          </h1>
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-black tracking-[0.25em] text-cyan-300/80">
            <BarChart3 className="w-3 h-3" />
            रिज़ल्ट हिस्ट्री देखें
          </div>
        </button>

        {/* Countdown pill removed per user request */}

        {/* ===== Tabs ===== */}
        <div className="relative mx-4 mt-4" data-testid="bet-type-tabs">
          <div className="grid grid-cols-3 gap-2 rounded-2xl border border-cyan-400/20 bg-black/40 p-1.5 backdrop-blur-sm">
            {TABS.map((t) => {
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`h-12 rounded-xl font-black tracking-widest text-sm transition active:scale-[0.97] ${
                    active ? "text-white shadow-[0_8px_24px_-6px_rgba(56,189,248,0.6)]" : "text-slate-400 hover:text-slate-200"
                  }`}
                  style={active ? { background: "linear-gradient(135deg,#22d3ee 0%,#2563eb 100%)" } : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ===== Active tab body ===== */}
        <div className="relative px-4 pt-4" data-testid={`active-body-${activeTab}`}>
          {activeTab === "jodi"      && <JodiBetPage embedded />}
          {activeTab === "haruf"     && <HarufPage embedded />}
          {activeTab === "cross_bet" && <CrossBetPage embedded />}
        </div>
      </div>

      {/* ===== Result History Sheet ===== */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto border-t-2 border-cyan-400/40"
          style={{ background: "linear-gradient(180deg, #0a1430 0%, #060a1b 100%)" }}>
          <div className="text-center mb-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
              style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#0b1230" }}>
              <TrendingUp className="w-3 h-3" /> Result History
            </div>
            <div className="font-display font-black text-2xl text-gold-static mt-2" data-testid="history-market-name">{market?.name}</div>
            <div className="text-xs text-cyan-200/70 mt-1">Past results (last 30 days)</div>
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="history-grid">
            {history.length === 0 ? (
              <div className="col-span-2 text-center text-cyan-200/60 text-sm py-6">No history yet — pehli baar result aane par yahaan dikhega</div>
            ) : history.map((row, i) => (
              <div key={i} className="rounded-xl p-2 flex items-center justify-between shadow-md"
                style={{ background: "rgba(10,24,51,0.65)", border: "1px solid rgba(34,211,238,0.25)" }}>
                <div className="text-xs text-cyan-200/80 font-medium">{row.date}</div>
                <div className="bg-gradient-to-br from-yellow-400 to-amber-500 text-blue-950 font-black text-xl px-3 py-1 rounded-lg tabular-nums shadow">{row.result}</div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
}
